import { API, postJSON } from "../../api.js";
import { optionsCache, renderTab } from "../../app/shell.js";
import { $, cls, esc, fmtArchetypeKpi, fmtMoney, opt } from "../../ui/format.js";
import { helpIcon } from "../../ui/help.js";
import { moneySliderMax, openAmountModal, openInvestSliderModal, sliderField, toast } from "../../ui/modals.js";
import { HEALTH_COLOR, openBuildFactoryModal, openUpgradeFactoryModal } from "./operations.js";

// Product OS: Command Center drill-down state, scoped to the Products tab —
// not a new top-level nav item (see renderProducts/renderProductCommandCenter).
let productCCId = null;

let productCCTab = "command";


// Product OS Phase 8 (Portfolio matrix + filters): local, in-tab state —
// not persisted, resets on tab switch, same idiom as productCCTab above.
let productFilterStage = "all";

let productFilterCategory = "all";

let productSortBy = "revenue_desc";




function ccTabMarket(p, market, archetypePanel) {
  if (market.error) return `<p style="color:var(--text-mid);">${esc(market.error)}</p>`;
  return `
    <div class="card-grid" style="margin-bottom:16px;">
      <div class="card"><div class="card-label">TAM (monthly, full geography)</div><div class="card-value">${fmtMoney(market.tam_monthly_revenue)}</div></div>
      <div class="card"><div class="card-label">SAM (monthly, current footprint)</div><div class="card-value">${fmtMoney(market.sam_monthly_revenue)}</div></div>
      <div class="card"><div class="card-label">Penetration</div><div class="card-value">${market.penetration_pct}%</div></div>
      <div class="card"><div class="card-label">Geographic Coverage</div><div class="card-value">${market.geographic_coverage_pct}%</div></div>
      <div class="card"><div class="card-label">Market Growth</div><div class="card-value">${market.market_growth_pct_monthly}%/mo</div></div>
    </div>
    <div style="max-width:520px; margin-bottom:6px;">
      <div style="height:8px; background:rgba(255,255,255,0.08); border-radius:4px; overflow:hidden;">
        <div style="width:${Math.min(100, market.penetration_pct)}%; height:100%; background:${market.penetration_pct >= 65 ? "var(--brass)" : "var(--sage)"};"></div>
      </div>
    </div>
    <p style="font-size:12.5px; color:var(--text-mid); max-width:600px;">${esc(market.saturation_note)}</p>
    <p style="font-size:11.5px; color:var(--text-low); max-width:600px; margin-top:10px;">TAM/SAM are computed from this product's own launch-month run-rate, an archetype-specific headroom multiple (${esc(archetypePanel.archetype_label || "")} products get a different multiple than, say, a capacity-bound hotel), and how many countries it has actually been localized into — the exact same ceiling that throttles this product's real monthly revenue, not a separate marketing estimate.</p>
  `;
}

function ccTabCustomers(p) {
  return `
    <div class="card-grid" style="margin-bottom:16px;">
      <div class="card"><div class="card-label">${esc(p.customers_label || "Customers")}</div><div class="card-value">${p.estimated_customers.toLocaleString("en-US")}</div></div>
      <div class="card"><div class="card-label">${esc(p.churn_label || "Churn")}</div><div class="card-value ${p.churn_rate_pct > 3 ? "neg" : "pos"}">${p.churn_rate_pct}%</div></div>
      <div class="card"><div class="card-label">${esc(p.satisfaction_label || "Satisfaction")}</div><div class="card-value">${p.satisfaction}/100</div></div>
      <div class="card"><div class="card-label">Expansion</div><div class="card-value pos">+${p.expansion_rate_pct}%/mo</div></div>
    </div>
    <h3 style="font-family:var(--font-display); font-size:15px; margin-bottom:8px;">Segment Mix</h3>
    <table class="ledger">
      <tr><th>Segment</th><th>${esc(p.customers_label || "Customers")}</th></tr>
      ${Object.entries(p.segments || {}).map(([seg, n]) => `<tr><td>${esc(seg)}</td><td class="mono">${n.toLocaleString("en-US")}</td></tr>`).join("")}
    </table>
    <p style="font-size:11.5px; color:var(--text-low); margin-top:10px;">Segment mix is driven by this product's price/quality tier — upgrading tier shifts the mix toward higher-value, lower-churn segments (see Pricing tab).</p>
  `;
}

function ccTabPricing(p) {
  return `
    <div class="card-grid" style="margin-bottom:16px;">
      <div class="card"><div class="card-label">Current Tier</div><div class="card-value" style="font-size:16px;">${esc(p.tier_label || p.tier)}</div></div>
      <div class="card"><div class="card-label">ARPU</div><div class="card-value">${fmtMoney(p.arpu)}</div></div>
      <div class="card"><div class="card-label">Price vs. Launch</div><div class="card-value ${cls(p.price_adjustment_pct)}">${p.price_adjustment_pct > 0 ? "+" : ""}${p.price_adjustment_pct}%</div></div>
    </div>
    <p style="font-size:12.5px; color:var(--text-mid); max-width:600px;">${esc(p.tier_description || "")}</p>
    ${p.elasticity_active ? `<p style="font-size:11.5px; color:var(--brass); margin-top:8px;">A recent price change is still working through demand (responds over 4 months).</p>` : ""}
    ${p.next_tier ? `
      <div style="margin-top:14px; padding:12px; border:1px solid var(--line); border-radius:8px;">
        <div style="font-size:12px; color:var(--text-mid);">Next tier: <strong style="color:var(--brass);">${esc(p.next_tier_label)}</strong></div>
        <div style="font-size:11.5px; color:var(--text-low); margin-top:4px;">${esc(p.next_tier_description)}</div>
      </div>
    ` : `<p style="font-size:11.5px; color:var(--text-low); margin-top:14px;">Already at the top tier.</p>`}
    <h3 style="font-family:var(--font-display); font-size:15px; margin:20px 0 8px;">Decisions</h3>
    <div id="cc-actions-host"></div>
  `;
}

function ccTabRoadmap(p) {
  return `
    <div id="roadmap-content">Loading roadmap…</div>
    <h3 style="font-family:var(--font-display); font-size:15px; margin:16px 0 8px;">Decisions</h3>
    <div id="cc-actions-host"></div>
  `;
}

async function wireProductRoadmap(p) {
  const r = await (await fetch(`${API}/products/${p.id}/roadmap`)).json();
  const host = $("roadmap-content");
  if (!host) return;
  const effectLabel = (f) => ({
    arpu_boost: `+${(f.magnitude * 100).toFixed(0)}% ARPU`,
    satisfaction_boost: `+${f.magnitude.toFixed(0)} satisfaction`,
    churn_reduction: `-${(f.magnitude * 100).toFixed(1)}% churn`,
  }[f.effect_type] || f.effect_type);

  host.innerHTML = `
    <h3 style="font-family:var(--font-display); font-size:15px; margin-bottom:8px;">In Development (${r.capacity.in_use}/${r.capacity.max_concurrent} engineering slots in use)</h3>
    ${r.building.length ? `
      <table class="ledger" style="margin-bottom:18px;">
        <tr><th>Feature</th><th>Effect on ship</th><th>Progress</th><th>Expected</th></tr>
        ${r.building.map((f) => `
          <tr>
            <td>${esc(f.name)}</td>
            <td>${esc(effectLabel(f))}</td>
            <td class="mono">
              <div style="width:90px; height:5px; background:rgba(255,255,255,0.08); border-radius:3px; overflow:hidden; display:inline-block; vertical-align:middle; margin-right:6px;">
                <div style="width:${Math.min(100, 100 * f.months_elapsed / f.build_months)}%; height:100%; background:var(--brass);"></div>
              </div>${f.months_elapsed}/${f.build_months}mo
            </td>
            <td class="mono">M${f.expected_ship_month}${f.delay_months > 0 ? ` <span style="color:var(--oxblood);">(+${f.delay_months} slip)</span>` : ""}</td>
          </tr>
        `).join("")}
      </table>
    ` : `<p style="color:var(--text-mid); margin-bottom:18px;">Nothing currently in development.</p>`}

    <h3 style="font-family:var(--font-display); font-size:15px; margin-bottom:8px;">Shipped</h3>
    ${r.shipped.length ? `
      <table class="ledger" style="margin-bottom:18px;">
        <tr><th>Feature</th><th>Effect</th><th>Shipped</th></tr>
        ${r.shipped.map((f) => `<tr><td>${esc(f.name)}</td><td>${esc(effectLabel(f))}</td><td class="mono">M${f.ship_month}${f.delay_months > 0 ? ` <span style="color:var(--text-low);">(slipped ${f.delay_months}mo)</span>` : ""}</td></tr>`).join("")}
      </table>
    ` : `<p style="color:var(--text-mid); margin-bottom:18px;">No features shipped yet.</p>`}

    <h3 style="font-family:var(--font-display); font-size:15px; margin-bottom:8px;">Available to Start</h3>
    ${r.available.length ? `
      <table class="ledger">
        <tr><th>Feature</th><th>Effect on ship</th><th>Build time</th><th>Cost</th><th></th></tr>
        ${r.available.map((f) => `
          <tr>
            <td>${esc(f.name)}${f.blocked_by.length ? `<br><span style="font-size:10.5px; color:var(--oxblood);">Needs: ${f.blocked_by.map(esc).join(", ")}</span>` : ""}</td>
            <td>${esc(effectLabel(f))}</td>
            <td class="mono">~${f.build_months}mo</td>
            <td class="mono">${fmtMoney(f.cost)}</td>
            <td>${f.blocked_by.length ? "" : `<button data-start-feature="${esc(f.name)}" class="primary" style="font-size:11px; padding:4px 10px;">Start</button>`}</td>
          </tr>
        `).join("")}
      </table>
    ` : `<p style="color:var(--text-mid);">All roadmap features shipped for this product.</p>`}
  `;

  host.querySelectorAll("[data-start-feature]").forEach((btn) => {
    btn.addEventListener("click", async () => {
      const res = await postJSON("/products/purchase-feature", { product_id: p.id, feature_name: btn.dataset.startFeature });
      toast(res.message);
      renderTab();
    });
  });
}

function ccTabOperations(p) {
  return `
    <div class="card-grid" style="margin-bottom:16px;">
      <div class="card"><div class="card-label">Production Model</div><div class="card-value" style="font-size:15px;">${p.has_factory ? "Factory-based" : "Service / no factory"}</div></div>
      <div class="card"><div class="card-label">Quality Investment</div><div class="card-value">${p.quality_investment_level}/100</div></div>
      <div class="card"><div class="card-label">Support Campaign</div><div class="card-value" style="font-size:15px;">${p.support_boost_active ? "Active" : "Inactive"}</div></div>
    </div>
    ${!p.has_factory ? `<p style="font-size:12px; color:var(--text-low);">No factory attached — defect/reliability risk is managed purely through Quality Control investment instead of a physical production line. Build a factory from the Portfolio card if this product needs a real capacity ceiling.</p>` : `<p style="font-size:12px; color:var(--text-low);">Factory details (utilization, maintenance, automation, supplier) are on the Factories &amp; Supply Chain panel below the Portfolio list.</p>`}
    <h3 style="font-family:var(--font-display); font-size:15px; margin:20px 0 8px;">Decisions</h3>
    <div id="cc-actions-host"></div>
  `;
}

function ccTabFinancials(p) {
  return `
    <table class="ledger" style="margin-bottom:16px;">
      <tr><th>Line</th><th>Value</th></tr>
      <tr><td>Revenue</td><td class="mono">${fmtMoney(p.monthly_revenue)}</td></tr>
      <tr><td>Variable Cost / Unit</td><td class="mono">${fmtMoney(p.variable_cost_per_unit)}</td></tr>
      <tr><td>Contribution Margin (per unit)</td><td class="mono ${cls(p.contribution_margin)}">${fmtMoney(p.contribution_margin)}</td></tr>
      <tr><td>Fixed Cost / mo (amortized investment)</td><td class="mono">${fmtMoney(p.fixed_cost_monthly)}</td></tr>
      <tr><td>Monthly Profit (contribution basis)</td><td class="mono ${cls(p.monthly_profit)}">${fmtMoney(p.monthly_profit)}</td></tr>
      <tr><td>Breakeven ${esc(p.customers_label || "Customers")}</td><td class="mono">${p.breakeven_customers !== null ? p.breakeven_customers.toLocaleString("en-US") : "n/a"}</td></tr>
      <tr><td>Above Breakeven</td><td class="mono">${p.above_breakeven === true ? "Yes ✅" : p.above_breakeven === false ? "No ⚠️" : "n/a"}</td></tr>
    </table>
    <p style="font-size:11px; color:var(--text-low);">Company-wide cost of goods, R&amp;D allocation, and full income-statement treatment live in the Financials tab and the accounting ledger — this is this product's own unit economics.</p>
  `;
}

function ccTabCompetition(p, competitors) {
  const active = (competitors || []).filter((c) => c.status !== "Bankrupt" && c.status !== "Acquired");
  return `
    <p style="font-size:12px; color:var(--text-low); margin-bottom:12px;">Competitors are tracked at the company level, not per-product — this is the same competitive landscape shown on the Competitors tab, for context while making decisions on this product.</p>
    <table class="ledger">
      <tr><th>Competitor</th><th>Strategy</th><th>Market Share</th><th>Status</th></tr>
      ${active.map((c) => `<tr><td>${esc(c.name)}</td><td>${esc(c.strategy)}</td><td class="mono">${c.market_share_pct}%</td><td>${esc(c.status)}</td></tr>`).join("") || `<tr><td colspan="4" style="color:var(--text-low);">No active competitors tracked yet.</td></tr>`}
    </table>
    ${p.cannibalization_active ? `
      <h3 style="font-family:var(--font-display); font-size:15px; margin:20px 0 8px;">Internal Cannibalization</h3>
      <p style="font-size:12.5px; color:var(--oxblood);">Currently losing ${Math.abs(p.cannibalization_effect_pct)}%/mo of growth to overlapping products in your own portfolio: ${(p.cannibalized_by || []).map((c) => esc(c.product_name)).join(", ")}.</p>
    ` : ""}
  `;
}

function ccTabGeography(p, market) {
  return `
    <div class="card-grid" style="margin-bottom:16px;">
      <div class="card"><div class="card-label">Countries Localized</div><div class="card-value">${p.countries.length}</div></div>
      <div class="card"><div class="card-label">Geographic Coverage of TAM</div><div class="card-value">${market.geographic_coverage_pct || "—"}%</div></div>
    </div>
    ${p.countries.length ? `
      <div style="display:flex; flex-wrap:wrap; gap:8px;">
        ${p.countries.map((c) => `<span style="font-size:12px; font-family:var(--font-mono); padding:4px 10px; border:1px solid var(--line); border-radius:12px; color:var(--text-mid);">🌍 ${esc(c)}</span>`).join("")}
      </div>
    ` : `<p style="color:var(--text-mid);">Not yet localized beyond the home market. Each additional country widens this product's serviceable addressable market (see Market tab).</p>`}
    <h3 style="font-family:var(--font-display); font-size:15px; margin:20px 0 8px;">Decisions</h3>
    <div id="cc-actions-host"></div>
  `;
}

function ccTabLifecycle(p) {
  const stages = ["Development", "Launch", "Growth", "Maturity", "Decline"];
  const stage = p.status;  // backend-computed, condition-driven — see engine._product_lifecycle_stage
  const isTerminal = stage === "Sunsetting" || stage === "Retired";
  return `
    ${isTerminal ? `
      <div style="padding:10px 14px; border:1px solid var(--oxblood); border-radius:8px; color:var(--oxblood); font-size:12.5px; margin-bottom:16px;">${esc(stage)} — ${esc(p.lifecycle_reason || "")}</div>
    ` : `
      <div style="display:flex; gap:6px; margin-bottom:8px;">
        ${stages.map((s) => `<div style="flex:1; text-align:center; padding:8px 4px; font-size:11px; font-family:var(--font-mono); border-radius:6px; background:${s === stage ? "rgba(126,232,250,0.15)" : "rgba(255,255,255,0.04)"}; color:${s === stage ? "var(--brass)" : "var(--text-low)"};">${s}</div>`).join("")}
      </div>
      <p style="font-size:12px; color:var(--text-mid); margin-bottom:16px;">${esc(p.lifecycle_reason || "")}</p>
    `}
    <table class="ledger" style="margin-bottom:16px;">
      <tr><th>Field</th><th>Value</th></tr>
      <tr><td>Status</td><td>${esc(p.status)}</td></tr>
      <tr><td>Months Live</td><td class="mono">${p.months_live}</td></tr>
      <tr><td>Tier</td><td>${esc(p.tier_label || p.tier)}</td></tr>
      <tr><td>Health</td><td class="mono">${p.health_score}/100</td></tr>
      <tr><td>Market Penetration</td><td class="mono">${p.market_penetration_pct}%</td></tr>
      ${p.sunset_month ? `<tr><td>Scheduled Retirement</td><td class="mono">M${p.sunset_month}</td></tr>` : ""}
    </table>
    <p style="font-size:11px; color:var(--text-low); margin-bottom:16px;">Stage is judged on real conditions (market penetration, growth rate, satisfaction), not just age — a product with strong metrics can stay in Growth well past when a purely age-based clock would have aged it into Maturity, and a struggling young product can hit Decline early.</p>
    <h3 style="font-family:var(--font-display); font-size:15px; margin-bottom:8px;">Decisions</h3>
    <div id="cc-actions-host"></div>
  `;
}

function _productNarrative(p) {
  if (!p.active) return `${p.name} has been retired and is no longer generating revenue.`;
  const unit = (p.customers_label || "Customers").toLowerCase();
  const churnWord = (p.churn_label || "Churn");
  const satWord = (p.satisfaction_label || "Satisfaction");
  const parts = [];
  if (p.above_breakeven === false) {
    parts.push(`${p.name} is currently running below breakeven — it needs ${(p.breakeven_customers - p.estimated_customers).toLocaleString("en-US")} more ${unit} at today's pricing to turn a profit. Every month below that line is pure burn on this line specifically.`);
  } else if (p.monthly_profit > 0) {
    parts.push(`${p.name} is solidly profitable, clearing ${fmtMoney(p.monthly_profit)}/mo above breakeven on ${p.estimated_customers.toLocaleString("en-US")} ${unit}.`);
  }
  if (p.churn_rate_pct > 3) {
    parts.push(`${churnWord} is running hot at ${p.churn_rate_pct}%/mo — at that pace, roughly a third of the base turns over every year, which caps growth no matter how hard Marketing pushes new signups in the front door.`);
  } else if (p.churn_rate_pct <= 1 && p.estimated_customers > 0) {
    parts.push(`${churnWord} is low at ${p.churn_rate_pct}%/mo — the base is sticking around, which is the healthiest possible foundation to grow from.`);
  }
  if (p.satisfaction < 50) {
    parts.push(`${satWord} sits at just ${p.satisfaction}/100 — a leading indicator that tends to show up as attrition a few months later if left unaddressed.`);
  }
  if (p.next_tier) {
    parts.push(`It's grown to the point where an upgrade to ${p.next_tier_label || p.next_tier} is available, which raises the price ceiling and opens new customer segments.`);
  }
  if (p.sunset_month) {
    parts.push(`This line is on a scheduled wind-down, fully retiring by month ${p.sunset_month} — no action needed, it's a graceful exit rather than a cliff.`);
  }
  if (!parts.length) parts.push(`${p.name} is tracking steadily — no red flags and no standout wins this month.`);
  return parts.join(" ");
}

function _findMenuAction(p, kind) {
  if (!p.actionMenu) return null;
  for (const cat of p.actionMenu.categories) {
    const found = cat.actions.find((a) => a.kind === kind);
    if (found) return found;
  }
  return null;
}

function _productRecommendedAction(p) {
  if (!p.active) return null;
  if (p.above_breakeven === false) {
    const a = _findMenuAction(p, "campaign");
    return { attr: "data-market", value: p.id, label: a ? a.label : "Market Harder",
             extra: `data-pname="${esc(p.name)}" data-label="${esc(a ? a.label : "Market Harder")}" data-desc="${esc(a ? a.description : "")}"` };
  }
  if (p.churn_rate_pct > 3) {
    const a = _findMenuAction(p, "support");
    return { attr: "data-support", value: p.id, label: a ? a.label : "Customer Support",
             extra: `data-pname="${esc(p.name)}" data-label="${esc(a ? a.label : "Customer Support")}" data-desc="${esc(a ? a.description : "")}"` };
  }
  if (p.satisfaction < 50) {
    const a = _findMenuAction(p, "feature");
    return { attr: "data-feature", value: p.id, label: a ? a.label : "Ship Feature",
             extra: `data-pname="${esc(p.name)}" data-desc="${a ? esc(a.description) : ""}"` };
  }
  if (p.next_tier) return { attr: "data-upgrade", value: p.id, label: `Upgrade to ${p.next_tier_label || p.next_tier}`, extra: `data-next-tier="${esc(p.next_tier)}"` };
  if (p.health_score >= 80 && p.above_breakeven) {
    const a = _findMenuAction(p, "expand");
    return { attr: "data-localize", value: p.id, label: a ? a.label : "Localize",
             extra: `data-pname="${esc(p.name)}" data-label="${esc(a ? a.label : "Localize")}" data-desc="${esc(a ? a.description : "")}"` };
  }
  return null;
}

function renderProductCard(p) {
  const healthCls = HEALTH_COLOR(p.health_score);
  const churnCls = p.churn_rate_pct > 3 ? "neg" : p.churn_rate_pct > 1 ? "" : "pos";
  const satisfactionCls = p.satisfaction >= 75 ? "pos" : p.satisfaction >= 50 ? "" : "neg";
  const stage = p.status;  // Product OS: backend is the single source of truth for lifecycle stage now (condition-driven, see engine._product_lifecycle_stage)
  const stages = ["Development", "Launch", "Growth", "Maturity", "Decline"];
  const gaugeColor = p.health_score >= 70 ? "var(--sage)" : p.health_score >= 40 ? "var(--brass)" : "var(--oxblood)";
  const circumference = 175.9; // 2 * pi * 28
  const dashOffset = circumference * (1 - Math.min(100, Math.max(0, p.health_score)) / 100);
  const recommended = _productRecommendedAction(p);
  return `
    <div class="product-card">
      <div class="product-card-top">
        <div class="product-card-name">${esc(p.name)} <span title="${esc(p.tier_description || "")}" style="font-size:11px; color:var(--text-low); font-family:var(--font-mono);">v${p.version} · [${esc(p.tier_label || p.tier)}]</span></div>
        <span class="status-pill ${p.active ? (p.status === "Sunsetting" ? "open" : "signed") : "rejected"}">${esc(p.status)}</span>
      </div>
      <div class="product-meta">${esc(p.category)} · Launched M${p.launch_month} · ${p.months_live} months live ${p.has_factory ? "· 🏭 Factory" : ""}</div>
      ${p.archetype_label ? `
        <div style="display:flex; flex-wrap:wrap; align-items:center; gap:6px; margin:6px 0 4px;">
          <span title="${esc(p.archetype_label)} economic model" style="font-size:10px; font-family:var(--font-mono); padding:3px 8px; border-radius:10px; border:1px solid var(--brass); color:var(--brass);">${esc(p.archetype_label)}</span>
          ${(p.archetype_kpis || []).map((k) => `<span title="${esc(k.note || "")}" style="font-size:10.5px; font-family:var(--font-mono); color:var(--text-mid);">${esc(k.label)}: <b style="color:var(--text-hi);">${fmtArchetypeKpi(k)}</b>${k.estimate ? " <span style=\"color:var(--text-low);\">(est.)</span>" : ""}</span>`).join(`<span style="color:var(--line);">·</span>`)}
        </div>
        ${p.market_penetration_pct !== undefined && p.market_penetration_pct !== null ? `
          <div title="Share of this product's serviceable addressable market already captured — growth throttles as this nears 100%." style="display:flex; align-items:center; gap:8px; margin:0 0 10px;">
            <div style="flex:1; height:4px; background:rgba(255,255,255,0.08); border-radius:2px; overflow:hidden;">
              <div style="width:${Math.min(100, p.market_penetration_pct)}%; height:100%; background:${p.market_penetration_pct >= 65 ? "var(--brass)" : "var(--sage)"};"></div>
            </div>
            <span style="font-size:10px; color:var(--text-low); font-family:var(--font-mono); white-space:nowrap;">${p.market_penetration_pct}% of SAM</span>
          </div>
        ` : ""}
        ${p.cannibalization_active ? `
          <div title="${esc((p.cannibalized_by || []).map((c) => `${c.product_name} (${c.similarity * 100}% overlap)`).join(", "))}" style="font-size:10.5px; font-family:var(--font-mono); color:var(--oxblood); margin:0 0 10px;">
            ⚠ ${p.cannibalization_effect_pct}%/mo pulled by ${(p.cannibalized_by || []).map((c) => esc(c.product_name)).join(", ") || "a newer sibling product"}
          </div>
        ` : ""}
      ` : ""}
      ${p.config && Object.keys(p.config).length ? `
        <div style="display:flex; flex-wrap:wrap; gap:6px; margin:8px 0 12px;">
          ${Object.entries(p.config).map(([dim, choice]) => `<span title="${esc(dim)}" style="font-size:10.5px; font-family:var(--font-mono); padding:3px 8px; border:1px solid var(--line); border-radius:10px; color:var(--text-mid); background:rgba(255,255,255,0.03);">${esc(choice)}</span>`).join("")}
          <span style="font-size:10.5px; font-family:var(--font-mono); padding:3px 8px; border-radius:10px; color:var(--text-low);">Config tier: ${Math.round((p.config_quality_tier || 0) * 100)}%</span>
        </div>
      ` : ""}
      ${p.marketing_config && Object.keys(p.marketing_config).length ? `
        <div style="display:flex; flex-wrap:wrap; gap:6px; margin:-4px 0 12px;">
          <span style="font-size:10px; color:var(--text-low); align-self:center;">📣</span>
          ${Object.entries(p.marketing_config).map(([dim, choice]) => `<span title="${esc(dim)}" style="font-size:10.5px; font-family:var(--font-mono); padding:3px 8px; border:1px solid var(--line); border-radius:10px; color:var(--brass); background:rgba(255,255,255,0.02);">${esc(choice)}</span>`).join("")}
          ${p.marketing_budget ? `<span style="font-size:10.5px; font-family:var(--font-mono); padding:3px 8px; border-radius:10px; color:var(--text-low);">Launch spend: ${fmtMoney(p.marketing_budget)}</span>` : ""}
        </div>
      ` : ""}
      ${p.launch_event === "hit" && p.event_boost_active ? `<div class="product-tip" style="border-left-color:var(--sage); color:var(--sage); font-style:normal; margin-bottom:10px;">🔥 Viral breakout hit — elevated growth from launch buzz is still active.</div>` : ""}
      ${p.launch_event === "flop" && p.event_boost_active ? `<div class="product-tip" style="border-left-color:var(--oxblood); color:var(--oxblood); font-style:normal; margin-bottom:10px;">📉 Landed as a flop at launch — growth is still dragging from the rocky reception.</div>` : ""}

      <div style="display:flex; gap:16px; align-items:center; margin:14px 0;">
        <svg width="68" height="68" viewBox="0 0 68 68" style="flex-shrink:0;">
          <circle cx="34" cy="34" r="28" fill="none" stroke="var(--line)" stroke-width="6"/>
          <circle cx="34" cy="34" r="28" fill="none" stroke="${gaugeColor}" stroke-width="6" stroke-linecap="round"
                  stroke-dasharray="${circumference}" stroke-dashoffset="${dashOffset}" transform="rotate(-90 34 34)"/>
          <text x="34" y="39" text-anchor="middle" font-size="15" font-weight="700" fill="var(--text-hi)" font-family="var(--font-mono)">${p.health_score}</text>
        </svg>
        <div style="flex:1;">
          <div style="font-size:11px; color:var(--text-low); font-family:var(--font-mono); margin-bottom:6px;">
            ${stages.map((s) => `<span style="${s === stage ? "color:var(--brass); font-weight:700;" : ""}">${s}</span>`).join(" → ")}
          </div>
          ${recommended ? `
            <div style="display:flex; align-items:center; gap:8px;">
              <span style="font-size:11px; color:var(--text-low);">Recommended:</span>
              <button class="primary" ${recommended.attr}="${recommended.value}" ${recommended.extra || ""} style="font-size:12px; padding:5px 10px;">${esc(recommended.label)}</button>
            </div>
          ` : ""}
        </div>
      </div>

      <div class="product-tip" style="border-left-color:var(--line); color:var(--text-mid); font-style:normal;">${esc(_productNarrative(p))}</div>

      <div style="display:grid; grid-template-columns:repeat(4,1fr); gap:8px; margin:12px 0;">
        <div style="background:rgba(0,0,0,0.15); border-radius:8px; padding:8px 10px;">
          <div style="font-size:10px; color:var(--text-low); text-transform:uppercase; letter-spacing:0.05em;">Revenue/mo</div>
          <div class="mono" style="font-size:15px;">${fmtMoney(p.monthly_revenue)}</div>
        </div>
        <div style="background:rgba(0,0,0,0.15); border-radius:8px; padding:8px 10px;">
          <div style="font-size:10px; color:var(--text-low); text-transform:uppercase; letter-spacing:0.05em;">Health</div>
          <div class="mono ${healthCls}" style="font-size:15px;">${p.health_score}/100</div>
        </div>
        <div style="background:rgba(0,0,0,0.15); border-radius:8px; padding:8px 10px;">
          <div style="font-size:10px; color:var(--text-low); text-transform:uppercase; letter-spacing:0.05em;">${esc(p.customers_label || "Customers")}</div>
          <div class="mono" style="font-size:15px;">${p.estimated_customers.toLocaleString("en-US")}</div>
        </div>
        <div style="background:rgba(0,0,0,0.15); border-radius:8px; padding:8px 10px;">
          <div style="font-size:10px; color:var(--text-low); text-transform:uppercase; letter-spacing:0.05em;">${esc(p.churn_label || "Churn")}</div>
          <div class="mono ${churnCls}" style="font-size:15px;">${p.churn_rate_pct}%</div>
        </div>
      </div>

      <table class="ledger" style="margin-bottom:10px;">
        <tr><th>Profit/mo</th><th>Growth</th><th>${esc(p.satisfaction_label || "Satisfaction")}</th></tr>
        <tr>
          <td class="mono ${cls(p.monthly_profit)}">${fmtMoney(p.monthly_profit)}</td>
          <td class="mono">${p.growth_pct_monthly}% ${p.marketing_boost_active ? "🔥" : ""}</td>
          <td class="mono ${satisfactionCls}">${p.satisfaction}/100</td>
        </tr>
      </table>

      <div style="margin-bottom:10px;">
        <div style="font-size:10px; color:var(--brass); font-family:var(--font-display); font-weight:700; text-transform:uppercase; letter-spacing:0.08em; margin-bottom:6px;">Customer Segments</div>
        <div style="display:flex; gap:14px; font-size:11.5px; font-family:var(--font-mono); color:var(--text-mid);">
          <span>Enterprise: ${(p.segments.Enterprise || 0).toLocaleString("en-US")}</span>
          <span>Mid-Market: ${(p.segments["Mid-Market"] || 0).toLocaleString("en-US")}</span>
          <span>SMB: ${(p.segments.SMB || 0).toLocaleString("en-US")}</span>
          ${p.expansion_rate_pct > 0 ? `<span class="pos">+${p.expansion_rate_pct}%/mo expansion</span>` : ""}
        </div>
      </div>
      ${p.countries.length ? `
        <div style="margin-bottom:10px;">
          <div style="font-size:10px; color:var(--brass); font-family:var(--font-display); font-weight:700; text-transform:uppercase; letter-spacing:0.08em; margin-bottom:6px;">Global Footprint (${p.countries.length} ${p.countries.length === 1 ? "market" : "markets"})</div>
          <div style="display:flex; flex-wrap:wrap; gap:6px;">
            ${p.countries.map((c) => `<span style="font-size:11px; font-family:var(--font-mono); padding:3px 9px; border:1px solid var(--line); border-radius:12px; color:var(--text-mid);">🌍 ${esc(c)}</span>`).join("")}
          </div>
        </div>
      ` : ""}
      <div class="product-meta">
        ARPU ${fmtMoney(p.arpu)} · Breakeven: ${p.breakeven_customers !== null ? p.breakeven_customers.toLocaleString("en-US") + " customers" : "n/a"}
        ${p.above_breakeven === true ? " ✅" : p.above_breakeven === false ? " ⚠️ below breakeven" : ""}
        ${!p.has_factory ? " · Quality Investment: " + p.quality_investment_level + "/100" : ""}
        ${p.support_boost_active ? " · 🎧 Support campaign active" : ""}
        ${p.price_adjustment_pct !== 0 ? ` · 💲 Price ${p.price_adjustment_pct > 0 ? "+" : ""}${p.price_adjustment_pct}% vs launch${p.elasticity_active ? " (demand still adjusting)" : ""}` : ""}
        ${p.sunset_month ? ` · ⏳ Sunsetting — fully retired by M${p.sunset_month}` : ""}
      </div>
      ${p.features.length ? `
        <div style="font-size:11px; color:var(--text-low); margin:8px 0;">
          <strong style="color:var(--text-mid);">Shipped:</strong>
          ${p.features.map((f) => `<span style="display:inline-block; margin-right:6px; padding:2px 7px; border:1px solid var(--line); border-radius:10px;">${esc(f.name)}</span>`).join("")}
        </div>
      ` : ""}
      ${p.features_building && p.features_building.length ? `
        <div style="font-size:11px; color:var(--brass); margin:8px 0;">
          <strong>Building:</strong>
          ${p.features_building.map((f) => `<span style="display:inline-block; margin-right:6px; padding:2px 7px; border:1px dashed var(--brass); border-radius:10px;">${esc(f.name)} (~M${f.start_month + f.build_months})</span>`).join("")}
        </div>
      ` : ""}
      <button data-open-cc="${p.id}" style="width:100%; margin-bottom:10px; font-size:12px; padding:7px; background:transparent; border:1px solid var(--brass); color:var(--brass);">Open Command Center →</button>
      ${p.active && p.actionMenu ? renderProductActionMenu(p) : ""}
    </div>
  `;
}

// Maps an action-menu "kind" (from get_product_action_menu in engine.py) to
// the data-* attribute wireProductCardEvents already listens for, so the
// exact same tested click handlers fire — only the label/description/
// eligibility shown to the player changes per industry.
const ACTION_KIND_ATTR = {
  campaign: "data-market", expand: "data-localize", price_up: "data-price-up",
  price_down: "data-price-down", support: "data-support", quality: "data-quality",
  feature: "data-feature", factory: "data-build-factory", relaunch: "data-relaunch",
  sunset: "data-sunset", retire: "data-retire",
};

function renderActionCard(a, p) {
  const attr = ACTION_KIND_ATTR[a.kind];
  if (!attr) return "";
  const cls = a.kind === "retire" ? "action-card danger" : "action-card";
  return `
    <button class="${cls}" ${attr}="${p.id}" data-pname="${esc(p.name)}"
            data-label="${esc(a.label)}" data-desc="${esc(a.description)}">
      <span class="action-card-label">${esc(a.label)}</span>
      <span class="action-card-desc">${esc(a.description)}</span>
    </button>
  `;
}

function renderProductActionMenu(p) {
  const menu = p.actionMenu;
  return menu.categories.map((cat) => {
    let actions = cat.actions;
    if (cat.key === "lifecycle") {
      actions = actions.filter((a) => !(a.kind === "sunset" && p.sunset_month));
    }
    const cards = actions.map((a) => renderActionCard(a, p)).join("");
    const growthUpgrade = (cat.key === "growth" && p.next_tier)
      ? `<button class="action-card highlight" data-upgrade="${p.id}" data-next-tier="${p.next_tier}">
           <span class="action-card-label">Upgrade to ${esc(p.next_tier_label || p.next_tier)}</span>
           <span class="action-card-desc">${esc(p.next_tier_description || "Raises the price ceiling and opens the next customer segment for this product.")}</span>
         </button>` : "";
    const qualityBlurb = (cat.key === "quality")
      ? `<div class="feature-roadmap-blurb">${esc(menu.features_blurb)}${p.has_factory ? " Capacity, supplier and automation are managed in Factories &amp; Supply Chain below." : ""}</div>`
      : "";
    return `
      <div class="action-category">
        <div class="action-category-header"><span class="action-category-title">${esc(cat.title)}</span></div>
        ${qualityBlurb}
        <div class="action-card-grid">${cards}${growthUpgrade}</div>
      </div>
    `;
  }).join("");
}

function wireProductCardEvents(list, products, categories) {
  const titleFor = (btn) => `${btn.dataset.label} — ${btn.dataset.pname}`;
  list.querySelectorAll("[data-market]").forEach((btn) => {
    btn.addEventListener("click", () => openAmountModal(titleFor(btn), `${btn.dataset.desc} How much to spend ($)?`, async (amount) => {
      const res = await postJSON("/products/market", { product_id: parseInt(btn.dataset.market), amount });
      await window.refreshAll();
      toast(res.message);
    }, false, 50000));
  });
  list.querySelectorAll("[data-upgrade]").forEach((btn) => {
    btn.addEventListener("click", async () => {
      const res = await postJSON("/products/upgrade-tier", { product_id: parseInt(btn.dataset.upgrade), target_tier: btn.dataset.nextTier });
      await window.refreshAll();
      toast(res.message);
    });
  });
  list.querySelectorAll("[data-price-up]").forEach((btn) => {
    btn.addEventListener("click", () => openAmountModal(titleFor(btn),
      `${btn.dataset.desc} Increase (%)? Demand responds with a lag over 4 months.`, async (pct) => {
        const res = await postJSON("/products/adjust-pricing", { product_id: parseInt(btn.dataset.priceUp), price_change_pct: Math.abs(pct) / 100 });
        await window.refreshAll();
        toast(res.message);
      }, false, 10));
  });
  list.querySelectorAll("[data-price-down]").forEach((btn) => {
    btn.addEventListener("click", () => openAmountModal(titleFor(btn),
      `${btn.dataset.desc} Decrease (%)? Demand responds with a lag over 4 months.`, async (pct) => {
        const res = await postJSON("/products/adjust-pricing", { product_id: parseInt(btn.dataset.priceDown), price_change_pct: -Math.abs(pct) / 100 });
        await window.refreshAll();
        toast(res.message);
      }, false, 10));
  });
  list.querySelectorAll("[data-support]").forEach((btn) => {
    btn.addEventListener("click", () => openAmountModal(titleFor(btn), `${btn.dataset.desc} Spend ($)? Lifts satisfaction for 4 months.`, async (amount) => {
      const res = await postJSON("/products/support", { product_id: parseInt(btn.dataset.support), amount });
      await window.refreshAll();
      toast(res.message);
    }, false, 30000));
  });
  list.querySelectorAll("[data-quality]").forEach((btn) => {
    btn.addEventListener("click", () => openAmountModal(titleFor(btn), `${btn.dataset.desc} Investment ($)? (1,500/point, permanent)`, async (amount) => {
      const res = await postJSON("/products/quality", { product_id: parseInt(btn.dataset.quality), amount });
      await window.refreshAll();
      toast(res.message);
    }, false, 50000));
  });
  list.querySelectorAll("[data-feature]").forEach((btn) => {
    btn.addEventListener("click", () => openFeatureModal(parseInt(btn.dataset.feature), btn.dataset.pname, btn.dataset.desc));
  });
  list.querySelectorAll("[data-relaunch]").forEach((btn) => {
    btn.addEventListener("click", () => openAmountModal(titleFor(btn), `${btn.dataset.desc} Investment ($)? Resets satisfaction and boosts growth for several months.`, async (amount) => {
      const res = await postJSON("/products/relaunch", { product_id: parseInt(btn.dataset.relaunch), investment: amount });
      await window.refreshAll();
      toast(res.message);
    }, false, 100000));
  });
  list.querySelectorAll("[data-localize]").forEach((btn) => {
    btn.addEventListener("click", () => openLocalizeModal(parseInt(btn.dataset.localize), btn.dataset.pname, btn.dataset.label, btn.dataset.desc));
  });
  list.querySelectorAll("[data-build-factory]").forEach((btn) => {
    btn.addEventListener("click", () => openBuildFactoryModal(parseInt(btn.dataset.buildFactory), btn.dataset.pname, btn.dataset.label, btn.dataset.desc));
  });
  list.querySelectorAll("[data-sunset]").forEach((btn) => {
    btn.addEventListener("click", () => openAmountModal(titleFor(btn), `${btn.dataset.desc} Wind down over how many months?`, async (months) => {
      const res = await postJSON("/products/schedule-sunset", { product_id: parseInt(btn.dataset.sunset), months_from_now: Math.max(1, Math.round(months)) });
      await window.refreshAll();
      toast(res.message);
    }, false, 3));
  });
  list.querySelectorAll("[data-retire]").forEach((btn) => {
    btn.addEventListener("click", async () => {
      if (!window.confirm(`${titleFor(btn)}\n\n${btn.dataset.desc}\n\nThis takes effect immediately and cannot be undone. Continue?`)) return;
      await postJSON("/products/retire", { product_id: parseInt(btn.dataset.retire) });
      await window.refreshAll();
      toast("Product retired immediately.");
    });
  });
}

function openFeatureModal(productId, productName, blurb) {
  fetch(`${API}/products/${productId}/features`).then((r) => r.json()).then((features) => {
    const backdrop = document.createElement("div");
    backdrop.className = "modal-backdrop";
    const EFFECT_LABEL = {
      arpu_boost: (m) => `+${(m * 100).toFixed(0)}% ARPU`,
      satisfaction_boost: (m) => `+${m.toFixed(0)} satisfaction`,
      churn_reduction: (m) => `-${(m * 100).toFixed(1)}% churn`,
    };
    backdrop.innerHTML = `
      <div class="modal" style="width:420px;">
        <h3>Feature Roadmap — ${esc(productName)}</h3>
        <p style="font-size:11px; color:var(--text-low); margin-bottom:10px;">Starting a feature begins a real build — its effect lands once it ships (see the Roadmap tab in Command Center for progress).</p>
        ${blurb ? `<div style="font-size:11.5px; color:var(--text-mid); font-style:italic; margin-bottom:14px;">${esc(blurb)}</div>` : ""}
        ${features.length ? `
          <table class="ledger" style="margin-bottom:16px;">
            ${features.map((f) => `
              <tr>
                <td class="label">${esc(f.name)}<br><span style="font-size:10.5px; color:var(--text-low);">${EFFECT_LABEL[f.effect_type](f.magnitude)} · ~${f.build_months}mo build${f.blocked_by && f.blocked_by.length ? `<br><span style="color:var(--oxblood);">Needs: ${f.blocked_by.map(esc).join(", ")}</span>` : ""}</span></td>
                <td class="mono">${fmtMoney(f.cost)}</td>
                <td>${f.blocked_by && f.blocked_by.length ? "" : `<button data-buy="${esc(f.name)}" class="primary">Start</button>`}</td>
              </tr>
            `).join("")}
          </table>
        ` : `<p style="color:var(--text-mid); margin-bottom:16px;">All available features shipped for this product.</p>`}
        <div class="modal-actions">
          <button id="modal-cancel">Close</button>
        </div>
      </div>
    `;
    document.body.appendChild(backdrop);
    backdrop.querySelector("#modal-cancel").addEventListener("click", () => backdrop.remove());
    backdrop.querySelectorAll("[data-buy]").forEach((btn) => {
      btn.addEventListener("click", async () => {
        backdrop.remove();
        const res = await postJSON("/products/purchase-feature", { product_id: productId, feature_name: btn.dataset.buy });
        await window.refreshAll();
        toast(res.message);
      });
    });
  });
}

function openLocalizeModal(productId, productName, label, description) {
  const backdrop = document.createElement("div");
  backdrop.className = "modal-backdrop";
  backdrop.innerHTML = `
    <div class="modal">
      <h3>${esc(label || "Expand Market")} — ${esc(productName)}</h3>
      ${description ? `<div style="font-size:11.5px; color:var(--text-mid); font-style:italic; margin-bottom:14px;">${esc(description)}</div>` : ""}
      <div class="field"><label>Country / Market</label><select id="modal-country">${opt(optionsCache.countries)}</select></div>
      ${sliderField("modal-investment", "Investment", { min: 0, max: moneySliderMax(80000), value: 80000 })}
      <div class="modal-actions">
        <button id="modal-cancel">Cancel</button>
        <button class="primary" id="modal-ok">${esc(label || "Expand")}</button>
      </div>
    </div>
  `;
  document.body.appendChild(backdrop);
  backdrop.querySelector("#modal-cancel").addEventListener("click", () => backdrop.remove());
  backdrop.querySelector("#modal-ok").addEventListener("click", async () => {
    const country_name = backdrop.querySelector("#modal-country").value.trim();
    const investment = parseFloat(backdrop.querySelector("#modal-investment").value) || 0;
    backdrop.remove();
    if (!country_name || investment <= 0) return;
    const res = await postJSON("/products/localize", { product_id: productId, country_name, investment });
    await window.refreshAll();
    toast(res.message);
  });
}

// Late-bound: a later view may replace these (window.X = ...), so callers use window.X.

export { ccTabCustomers, ccTabFinancials, ccTabMarket, ccTabRoadmap, wireProductRoadmap };
